import React, { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle } from 'lucide-react';

interface SamplePayloadViewerProps {
  payload: Record<string, unknown> | null | undefined;
}

const SamplePayloadViewer: React.FC<SamplePayloadViewerProps> = ({ payload }) => {
  const [expanded, setExpanded] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleKey = (keyPath: string) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(keyPath)) {
      newExpanded.delete(keyPath);
    } else {
      newExpanded.add(keyPath);
    }
    setExpandedKeys(newExpanded);
  };

  const renderValue = (value: unknown): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="text-primary">{value.toString()}</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-info">{value}</span>;
    }

    if (typeof value === 'string') {
      return <span className="text-success">"{value}"</span>;
    }

    if (Array.isArray(value)) {
      return (
        <span className="text-foreground">
          [{value.length} items]
        </span>
      );
    }

    if (typeof value === 'object') {
      return (
        <span className="text-foreground">
          {'{'}...{'}'}
        </span>
      );
    }

    return <span>{String(value)}</span>;
  };

  const renderObject = (
    obj: Record<string, unknown> | null | undefined,
    depth: number = 0,
    parentPath: string = '',
  ): JSX.Element => {
    return (
      <div className="space-y-1">
        {Object.entries(obj ?? {}).map(([key, value]) => {
          const keyPath = parentPath ? `${parentPath}.${key}` : key;
          const isExpandable = value !== null && typeof value === 'object';
          const isExpanded = expandedKeys.has(keyPath);

          return (
            <div key={key} style={{ marginLeft: `${depth * 16}px` }}>
              <div className="flex items-start space-x-2 py-1 hover:bg-muted rounded px-2">
                {isExpandable && (
                  <button
                    onClick={() => toggleKey(keyPath)}
                    className="text-muted-foreground hover:text-foreground mt-0.5"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                {!isExpandable && <div className="w-4" />}
                <div className="flex-1 font-mono text-sm">
                  <span className="text-primary font-semibold">
                    {key}
                  </span>
                  <span className="text-muted-foreground">: </span>
                  {!isExpanded && renderValue(value)}
                </div>
              </div>
              {isExpandable && isExpanded && (
                <div className="ml-4 mt-1 border-l-2 border-border pl-2">
                  {renderObject(value as Record<string, unknown>, depth + 1, keyPath)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-foreground">
          Sample Webhook Payload
        </h3>
        <div className="flex items-center space-x-2 text-success">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Sample Captured</span>
        </div>
      </div>

      <div className="bg-background border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center space-x-2 text-foreground hover:text-foreground mb-2"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <span className="font-mono text-sm font-semibold">payload</span>
        </button>
        {expanded && renderObject(payload, 0)}
      </div>

      <p className="mt-2 text-sm text-muted-foreground">
        This is the structure of the webhook data your service sends. Use the field paths below to map them to transaction fields.
      </p>
    </div>
  );
};

export default SamplePayloadViewer;

